import { useTranslation } from 'react-i18next';
import type { FieldValue, IRecord } from '@platform/core/types';
import { moduleBundle } from '@platform/i18n';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useDeleteRecordMutation, useListRecordsQuery } from '@platform/state/api';
import { ErrorBanner, Spinner } from '@platform/ui';
import { useValueText } from '../../fields/lookup/useValueText';
import { valueText } from '../../fields/options/valueText';
import { useConfirm } from '../../form/confirm/useConfirm';
import { recordCode } from '../../form/header/recordCode';
import type { ISubTableCardProps } from './ISubTableCardProps';
import { SubTableDraftRow } from './SubTableDraftRow';
import { subTableGrid } from './subTableGrid';
import { subTableLabel } from './subTableLabel';
import { SubTableRow } from './SubTableRow';
import { useSubTableAdd } from './useSubTableAdd';

/**
 * Картка підтаблиці другої секції «Подробиці» (прототип v4, subSecs): шапка «Підпис · N · + Додати» на --surface2, рядок підписів колонок,
 * рядки дочірнього типу з фільтром `<поле-посилання>:eq:<id батька>` за номером (новий — у кінці), «Ще нічого не додано.» без рядків.
 * Клітинки редагуються завжди, і в режимі перегляду батьківської форми (прототип: секція поза data-ro; без права зміни — лише читання);
 * «+ Додати» додає рядок на місці (useSubTableAdd; у ще не збереженого
 * батьківського запису — вимкнена з підказкою «Спершу збережіть запис»); × вилучає рядок — видаляє дочірній запис після підтвердження.
 */
export function SubTableCard({ ws, moduleKey, def, parent }: ISubTableCardProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const { confirm, dialog } = useConfirm();
  const { child, lookup, columns } = def;
  const fmt = useValueText(strings, child.key);
  const [remove, { error: removeError }] = useDeleteRecordMutation();
  const { data, isLoading, error } = useListRecordsQuery({ ws, rt: child.key, params: { filter: `${lookup.key}:eq:${parent.id}`, pageSize: 100, sort: 'code' } }, { skip: !parent.id });
  const add = useSubTableAdd(ws, def, parent.id);
  const canEdit = parent.permissions?.edit !== false;
  const unsaved = !parent.id;
  const grid = subTableGrid(columns, canEdit);
  const title = subTableLabel(def, moduleBundle(strings.lang, strings.moduleKey), strings.lang, strings.entity(child.key, 'name', child.name));
  const rows = data?.items ?? [];
  const removeRow = (r: IRecord) => {
    const value = (key: string) => (key === 'title' ? r.title : key === 'status' ? r.status : r.values[key]) as FieldValue | undefined;
    const row = columns.map((f) => valueText(f, child, value(f.key), fmt)).filter(Boolean).join(' · ');
    void confirm({ title: t('recordPage.general.removeRowTitle'), confirmLabel: t('recordPage.general.removeRowConfirm'), danger: true,
      text: t('recordPage.general.removeRowText', { row: row || recordCode(r.display, r.code), table: title }) })
      .then((ok) => { if (ok) void remove({ ws, rt: child.key, id: r.id }); });
  };
  return (
    <div className="rec-subcard">
      <div className="rec-subcard__head">
        <strong className="rec-subcard__name">{title}</strong>
        <span className="rec-relcard__count">{data ? data.total : unsaved ? 0 : ''}</span>
        {canEdit && (
          <button type="button" className="rec-relcard__link" disabled={add.busy || unsaved} title={unsaved ? t('recordPage.draft.saveFirst') : undefined} onClick={add.start}>
            {t('recordPage.general.add')}
          </button>
        )}
      </div>
      <ErrorBanner error={error ?? removeError ?? add.error} />
      <div className="rec-subcard__body">
        <div className="rec-subcard__row rec-subcard__row--head" style={grid}>
          {columns.map((f) => <span key={f.key}>{strings.field(child.key, f.key, f.label)}</span>)}{canEdit && <span />}
        </div>
        {rows.map((r) => (
          <SubTableRow key={r.id} ws={ws} child={child} record={r} fields={columns} grid={grid} readOnly={!canEdit}
            onRemove={canEdit && r.permissions?.delete !== false ? removeRow : undefined} />
        ))}
        {add.draft && (
          <SubTableDraftRow ws={ws} moduleKey={moduleKey} child={child} values={add.draft} fields={columns} grid={grid} onChange={add.setField} onDiscard={add.discard} />
        )}
      </div>
      {isLoading && <div className="rec-subcard__empty"><Spinner /></div>}
      {!isLoading && !rows.length && !add.draft && <div className="rec-subcard__empty">{t('recordPage.general.nothingAdded')}</div>}
      {dialog}
    </div>
  );
}
